// Firebase services for The Frosting Room
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  User,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { 
  getStorage, 
  ref, 
  uploadBytes, 
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import app from '../config/firebase';
import { Product, ProductCollection, ProductImage, StaffUser, StaffPermissions, StaffNotificationSettings, AvailabilityDate, AvailabilitySettings, TimeSlot, DeliveryTier, DeliveryPricingSettings, VatSettings, ImageOnCakeSettings, ColorPricingSettings, ProductCollectionType, normalizeAvailabilitySettings, resolveMaxBookingsForDate, createDefaultAvailabilitySettings, getDateKey, createLocalDate } from '../types';
import { createDefaultColorPricingSettings } from '../constants/cakeColors';
import { v4 as uuidv4 } from 'uuid';

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Authentication functions
export const signIn = (email: string, password: string) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  return signInWithPopup(auth, provider);
};

export const signUp = (email: string, password: string) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

export const logOut = () => {
  return signOut(auth);
};

export const resetPassword = (email: string) => {
  return sendPasswordResetEmail(auth, email);
};

export const updateUserProfile = (displayName?: string, photoURL?: string) => {
  if (!auth.currentUser) return Promise.reject('No user is signed in');
  return updateProfile(auth.currentUser, { displayName, photoURL });
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Firestore functions
export const createDocument = (collectionName: string, data: any) => {
  return addDoc(collection(db, collectionName), data);
};

export const setDocument = (collectionName: string, docId: string, data: any) => {
  return setDoc(doc(db, collectionName, docId), data);
};

export const getDocument = (collectionName: string, docId: string) => {
  return getDoc(doc(db, collectionName, docId));
};

export const getDocuments = (collectionName: string) => {
  return getDocs(collection(db, collectionName));
};

export const updateDocument = (collectionName: string, docId: string, data: any) => {
  return updateDoc(doc(db, collectionName, docId), data);
};

export const deleteDocument = (collectionName: string, docId: string) => {
  return deleteDoc(doc(db, collectionName, docId));
};

// Storage functions
export const uploadFile = async (path: string, file: File) => {
  try {
    console.log(`Starting upload for file: ${file.name} to path: ${path}`);
    
    // Create a full reference to the file with the correct bucket
    const storageRef = ref(storage, path);
    
    // Create file metadata including the content type
    const metadata = {
      contentType: file.type,
      customMetadata: {
        'uploaded-by': auth.currentUser?.uid || 'anonymous',
        'timestamp': new Date().toISOString()
      }
    };
    
    console.log('Uploading with metadata:', metadata);
    
    // Upload the file and metadata
    const uploadResult = await uploadBytes(storageRef, file, metadata);
    console.log('Upload successful:', uploadResult);
    
    return uploadResult;
  } catch (error) {
    console.error('Error uploading file:', error);
    // Log more details about the error
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
    throw error;
  }
};

export const getFileURL = (path: string) => {
  const storageRef = ref(storage, path);
  return getDownloadURL(storageRef);
};

export const deleteFile = (path: string) => {
  const storageRef = ref(storage, path);
  return deleteObject(storageRef);
};

// Collection references
export const usersCollection = collection(db, 'users');
export const ordersCollection = collection(db, 'orders');
export const productsCollection = collection(db, 'products');
export const collectionsCollection = collection(db, 'collections');
export const staffCollection = collection(db, 'staff');
export const availabilityCollection = collection(db, 'availability');
export const availabilitySettingsCollection = collection(db, 'availabilitySettings');
export const deliveryTiersCollection = collection(db, 'deliveryTiers');
export const deliverySettingsCollection = collection(db, 'deliverySettings');
export const vatSettingsCollection = collection(db, 'vatSettings');

// Product functions
export const getProducts = async (): Promise<Product[]> => {
  const snapshot = await getDocs(query(productsCollection, orderBy('createdAt', 'desc')));
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      description: data.description,
      collection: data.collection,
      collectionId: data.collectionId,
      images: data.images || [],
      flavors: data.flavors || [],
      sizes: data.sizes || [],
      availableShapes: data.availableShapes || [],
      isActive: data.isActive,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      // Include the optional fields
      availableLayers: data.availableLayers || [],
      maxFlavorsSelectable: data.maxFlavorsSelectable || 1,
      fillings: data.fillings || [],
      coverIcingOptions: data.coverIcingOptions || [],
      addOns: data.addOns || [],
      matchingCupcakes: data.matchingCupcakes || [],
      // New behavior flags with safe defaults (enabled by default for backward compatibility)
      enableBaseColor: data.enableBaseColor !== undefined ? data.enableBaseColor : true,
      enablePipingColor: data.enablePipingColor !== undefined ? data.enablePipingColor : true,
      enableMessageOnCake: data.enableMessageOnCake !== undefined ? data.enableMessageOnCake : true,
      enableImageOnCake: data.enableImageOnCake !== undefined ? data.enableImageOnCake : true,
      enableSampleImage: data.enableSampleImage !== undefined ? data.enableSampleImage : true
    } as Product;
  });
};

export const getProductsByCollection = async (collectionId: string): Promise<Product[]> => {
  const q = query(
    productsCollection, 
    where('collectionId', '==', collectionId), 
    where('isActive', '==', true),
    orderBy('name', 'asc')
  );
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      description: data.description,
      collection: data.collection,
      collectionId: data.collectionId,
      images: data.images || [],
      flavors: data.flavors || [],
      sizes: data.sizes || [],
      availableShapes: data.availableShapes || [],
      isActive: data.isActive,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      // Include the optional fields
      availableLayers: data.availableLayers || [],
      maxFlavorsSelectable: data.maxFlavorsSelectable || 1,
      fillings: data.fillings || [],
      coverIcingOptions: data.coverIcingOptions || [],
      addOns: data.addOns || [],
      matchingCupcakes: data.matchingCupcakes || [],
      // New behavior flags with safe defaults (enabled by default for backward compatibility)
      enableBaseColor: data.enableBaseColor !== undefined ? data.enableBaseColor : true,
      enablePipingColor: data.enablePipingColor !== undefined ? data.enablePipingColor : true,
      enableMessageOnCake: data.enableMessageOnCake !== undefined ? data.enableMessageOnCake : true,
      enableImageOnCake: data.enableImageOnCake !== undefined ? data.enableImageOnCake : true,
      enableSampleImage: data.enableSampleImage !== undefined ? data.enableSampleImage : true
    } as Product;
  });
};

export const getProduct = async (productId: string): Promise<Product | null> => {
  const docRef = doc(productsCollection, productId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  const data = docSnap.data();
  
  // Debug logging to see what's actually in the database
  console.log('Raw data from database:', data);
  console.log('Fillings from database:', data.fillings);
  console.log('Cover icing options from database:', data.coverIcingOptions);
  console.log('Available layers from database:', data.availableLayers);
  console.log('Max flavors selectable from database:', data.maxFlavorsSelectable);
  
  return {
    id: docSnap.id,
    name: data.name,
    description: data.description,
    collection: data.collection,
    collectionId: data.collectionId,
    images: data.images || [],
    flavors: data.flavors || [],
    sizes: data.sizes || [],
    availableShapes: data.availableShapes || [],
    isActive: data.isActive,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    // Include the optional fields
    availableLayers: data.availableLayers || [],
    maxFlavorsSelectable: data.maxFlavorsSelectable || 1,
    fillings: data.fillings || [],
    coverIcingOptions: data.coverIcingOptions || [],
    addOns: data.addOns || [],
    matchingCupcakes: data.matchingCupcakes || [],
    // New behavior flags with safe defaults (enabled by default for backward compatibility)
    enableBaseColor: data.enableBaseColor !== undefined ? data.enableBaseColor : true,
    enablePipingColor: data.enablePipingColor !== undefined ? data.enablePipingColor : true,
    enableMessageOnCake: data.enableMessageOnCake !== undefined ? data.enableMessageOnCake : true,
    enableImageOnCake: data.enableImageOnCake !== undefined ? data.enableImageOnCake : true,
    enableSampleImage: data.enableSampleImage !== undefined ? data.enableSampleImage : true
  } as Product;
};

export const createProduct = async (productData: Partial<Product>): Promise<Product> => {
  // Create a deep copy of productData without the images field
  const { images, ...productDataWithoutImages } = productData;
  
  // Upload any images that are File objects
  const processedImages = await processProductImages(images || []);
  
  // Create a clean product object without undefined values
  const cleanProductData: Record<string, any> = {
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  // Add all non-undefined fields from productDataWithoutImages
  console.log('🔍 Firebase createProduct - Input productDataWithoutImages:', productDataWithoutImages);
  
  Object.entries(productDataWithoutImages).forEach(([key, value]) => {
    console.log(`🔍 Processing field: ${key} = ${value} (type: ${typeof value})`);
    if (value === undefined) {
      console.error(`❌ UNDEFINED VALUE DETECTED in Firebase createProduct: ${key} = ${value}`);
    } else if (key !== 'id') {
      cleanProductData[key] = value;
      console.log(`✅ Added field to cleanProductData: ${key} = ${value}`);
    }
  });
  
  // Add processed images
  cleanProductData.images = processedImages;
  console.log('✅ Added processed images:', processedImages);
  
  console.log('🔍 Firebase createProduct - Final cleanProductData:', cleanProductData);
  console.log('Creating product with data:', JSON.stringify(cleanProductData, null, 2));
  
  const docRef = await addDoc(productsCollection, cleanProductData);
  
  return {
    ...cleanProductData,
    id: docRef.id,
    createdAt: new Date(),
    updatedAt: new Date()
  } as Product;
};

export const updateProduct = async (productId: string, productData: Partial<Product>): Promise<void> => {
  const docRef = doc(productsCollection, productId);
  
  // Create a clean update object without any undefined values
  const updateData: Record<string, any> = {
    updatedAt: serverTimestamp()
  };
  
  // Process images separately if they exist
  let processedImages;
  if (productData.images) {
    // Create a deep copy of productData without the images field
    const { images, ...productDataWithoutImages } = productData;
    
    // Process the images
    processedImages = await processProductImages(images);
    
    // Add other product data to the update object
    Object.entries(productDataWithoutImages).forEach(([key, value]) => {
      if (value !== undefined && key !== 'id') {
        updateData[key] = value;
      }
    });
  } else {
    // If no images, just add all other fields
    Object.entries(productData).forEach(([key, value]) => {
      if (value !== undefined && key !== 'id') {
        updateData[key] = value;
      }
    });
  }
  
  // Add processed images if they exist
  if (processedImages) {
    // Create a clean version of the images without the file property
    const cleanImages = processedImages.map(img => {
      // Create a new object without the file property
      const { file, ...cleanImage } = img;
      return cleanImage;
    });
    
    updateData.images = cleanImages;
  }
  
  console.log('Updating product with data:', JSON.stringify(updateData, null, 2));
  
  await updateDoc(docRef, updateData);
};

export const deleteProduct = async (productId: string): Promise<void> => {
  // Get the product to delete its images
  const product = await getProduct(productId);
  
  if (product && product.images && product.images.length > 0) {
    // Delete all product images from storage
    await Promise.all(product.images.map(async (image) => {
      try {
        // Only delete if the image path is in storage (not an external URL)
        if (image.url.includes('firebasestorage')) {
          const imagePath = `products/${productId}/${image.id}`;
          await deleteFile(imagePath);
        }
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }));
  }
  
  // Delete the product document
  await deleteDoc(doc(productsCollection, productId));
};

export const duplicateProduct = async (productId: string): Promise<Product> => {
  // Get the original product
  const originalProduct = await getProduct(productId);
  
  if (!originalProduct) {
    throw new Error('Product not found');
  }
  
  // Create a copy of the product data without the id and timestamps
  const { id, createdAt, updatedAt, ...productData } = originalProduct;
  
  // Modify the name to indicate it's a copy
  const duplicatedProductData = {
    ...productData,
    name: `${productData.name} (Copy)`,
    isActive: false, // Set as inactive by default so it can be reviewed before going live
  };
  
  // Create the new product
  const newProduct = await createProduct(duplicatedProductData);
  
  return newProduct;
};

// Helper function to process product images (upload files if needed)
export const processProductImages = async (images: ProductImage[]): Promise<ProductImage[]> => {
  const processedImages: ProductImage[] = [];
  
  for (const image of images) {
    try {
      // If the image is already processed (has a URL that's not a blob URL), add it as is
      // but make sure to create a clean copy without the file property
      if (image.url && !image.url.startsWith('blob:')) {
        // Create a clean copy without the file property
        const { file, ...cleanImage } = image;
        processedImages.push(cleanImage);
        continue;
      }
      
      // Generate unique ID for the image if it doesn't have one
      const imageId = image.id || uuidv4();
      
      // Skip if we don't have a file to upload
      if (!image.file) {
        console.warn('Image has no file to upload:', image);
        continue;
      }
      
      // Upload to Firebase Storage with a simplified path to avoid CORS issues
      const imagePath = `products/${imageId}`;
      
      // Log the upload attempt
      console.log('Uploading image to Firebase Storage:', { 
        imageId, 
        fileName: image.file.name,
        fileType: image.file.type,
        fileSize: image.file.size
      });
      
      // Upload the file directly without any processing
      const uploadResult = await uploadFile(imagePath, image.file);
      console.log('Upload successful:', uploadResult);
      
      // Get the download URL
      const downloadURL = await getFileURL(imagePath);
      console.log('Got download URL:', downloadURL);
      
      // Create a clean image object without the file property
      const { file, ...imageWithoutFile } = image;
      
      // Add processed image to the array
      processedImages.push({
        ...imageWithoutFile,
        id: imageId,
        url: downloadURL
      });
    } catch (error) {
      console.error('Error processing image:', error);
    }
  }
  
  return processedImages;
};

// Collection functions
export const getCollections = async (): Promise<ProductCollection[]> => {
  const snapshot = await getDocs(collectionsCollection);

  const collections = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name,
      description: data.description,
      order: typeof data.order === 'number' ? data.order : 999,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as ProductCollection & { order: number };
  });

  return collections.sort((a, b) => a.order - b.order) as ProductCollection[];
};

export const createCollection = async (collectionData: Partial<ProductCollection>): Promise<ProductCollection> => {
  const newCollection = {
    ...collectionData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  const docRef = await addDoc(collectionsCollection, newCollection);
  
  return {
    ...newCollection,
    id: docRef.id,
    createdAt: new Date(),
    updatedAt: new Date()
  } as ProductCollection;
};

export const updateCollection = async (collectionId: string, collectionData: Partial<ProductCollection>): Promise<void> => {
  const docRef = doc(collectionsCollection, collectionId);
  
  await updateDoc(docRef, {
    ...collectionData,
    updatedAt: serverTimestamp()
  });
};

export const deleteCollection = async (collectionId: string): Promise<void> => {
  // Check if there are products in this collection
  const products = await getProductsByCollection(collectionId);
  
  if (products.length > 0) {
    throw new Error('Cannot delete collection with existing products');
  }
  
  // Delete the collection document
  await deleteDoc(doc(collectionsCollection, collectionId));
};

// Staff management functions
export const getStaffUser = async (uid: string): Promise<StaffUser | null> => {
  const docRef = doc(staffCollection, uid);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  const data = docSnap.data();
  return {
    id: docSnap.id,
    uid: data.uid,
    email: data.email,
    displayName: data.displayName,
    firstName: data.firstName,
    lastName: data.lastName,
    role: data.role,
    permissions: data.permissions,
    notificationSettings: data.notificationSettings,
    phoneNumber: data.phoneNumber,
    photoURL: data.photoURL,
    isActive: data.isActive,
    lastLogin: data.lastLogin?.toDate() || new Date(),
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  } as StaffUser;
};

export const getAllStaffUsers = async (): Promise<StaffUser[]> => {
  const snapshot = await getDocs(query(staffCollection, orderBy('createdAt', 'desc')));
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      permissions: data.permissions,
      notificationSettings: data.notificationSettings,
      phoneNumber: data.phoneNumber,
      photoURL: data.photoURL,
      isActive: data.isActive,
      lastLogin: data.lastLogin?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as StaffUser;
  });
};

export const createStaffUser = async (userData: Partial<StaffUser>): Promise<StaffUser> => {
  if (!userData.uid) {
    throw new Error('Staff user must have a uid');
  }
  
  // Set default values for new staff user
  const defaultPermissions: StaffPermissions = {
    canManageProducts: true,
    canManageOrders: true,
    canManageCustomers: true,
    canManageStaff: userData.role === 'admin',
    canViewAnalytics: true,
    canManageSettings: userData.role === 'admin'
  };
  
  const defaultNotificationSettings: StaffNotificationSettings = {
    emailNotifications: true,
    orderUpdates: true,
    productUpdates: true,
    customerUpdates: true,
    marketingUpdates: false
  };
  
  const newStaffUser = {
    ...userData,
    permissions: userData.permissions || defaultPermissions,
    notificationSettings: userData.notificationSettings || defaultNotificationSettings,
    isActive: userData.isActive !== undefined ? userData.isActive : true,
    lastLogin: userData.lastLogin || serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  // Use the uid as the document ID
  await setDoc(doc(staffCollection, userData.uid), newStaffUser);
  
  return {
    ...newStaffUser,
    id: userData.uid,
    createdAt: new Date(),
    updatedAt: new Date(),
    lastLogin: new Date()
  } as StaffUser;
};

export const updateStaffUser = async (uid: string, userData: Partial<StaffUser>): Promise<void> => {
  const docRef = doc(staffCollection, uid);
  
  // Process profile image if provided
  let photoURL = userData.photoURL;
  if (photoURL && photoURL.startsWith('blob:')) {
    try {
      // Convert blob URL to File object
      const response = await fetch(photoURL);
      const blob = await response.blob();
      const file = new File([blob], 'profile.jpg', { type: blob.type });
      
      // Upload to Firebase Storage
      const imagePath = `staff/${uid}/profile`;
      await uploadFile(imagePath, file);
      
      // Get the download URL
      photoURL = await getFileURL(imagePath);
      
      // Update auth profile if the current user is updating their own profile
      if (auth.currentUser && auth.currentUser.uid === uid) {
        await updateUserProfile(undefined, photoURL);
      }
    } catch (error) {
      console.error('Error processing profile image:', error);
    }
  }
  
  await updateDoc(docRef, {
    ...userData,
    photoURL,
    updatedAt: serverTimestamp()
  });
};

export const updateStaffLastLogin = async (uid: string): Promise<void> => {
  const docRef = doc(staffCollection, uid);
  
  try {
    await updateDoc(docRef, {
      lastLogin: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating last login:', error);
  }
};

export const deleteStaffUser = async (uid: string): Promise<void> => {
  // Delete profile image if exists
  try {
    await deleteFile(`staff/${uid}/profile`);
  } catch (error) {
    // Ignore error if image doesn't exist
  }
  
  // Delete staff document
  await deleteDoc(doc(staffCollection, uid));
};

// Helper function to check if a user is a staff member
export const isStaffMember = async (uid: string): Promise<boolean> => {
  if (!uid) return false;
  
  const docRef = doc(staffCollection, uid);
  const docSnap = await getDoc(docRef);
  
  return docSnap.exists();
};

// Helper function to create admin user on Google sign-in if it doesn't exist
export const createAdminUserIfNeeded = async (user: User): Promise<StaffUser> => {
  const staffUser = await getStaffUser(user.uid);
  
  if (staffUser) {
    // Update last login time
    await updateStaffLastLogin(user.uid);
    return staffUser;
  }
  
  // Create new admin user
  const newStaffUser: Partial<StaffUser> = {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    firstName: user.displayName ? user.displayName.split(' ')[0] : '',
    lastName: user.displayName ? user.displayName.split(' ').slice(1).join(' ') : '',
    role: 'admin', // First user is always admin
    photoURL: user.photoURL || undefined,
    isActive: true
  };
  
  return createStaffUser(newStaffUser);
};

// ============================================================================
// AVAILABILITY FUNCTIONS - Calendar and Booking Management
// ============================================================================

// Helper function to convert Date to Firestore Timestamp
const dateToTimestamp = (date: Date) => {
  return Timestamp.fromDate(date);
};

// Helper function to convert Firestore Timestamp to Date
const timestampToDate = (timestamp: any) => {
  return timestamp?.toDate() || new Date();
};

const toLocalCalendarDate = (date: Date): Date => {
  return createLocalDate(date.getFullYear(), date.getMonth(), date.getDate());
};

const createAvailabilityDateId = (date: Date): string => {
  return getDateKey(date);
};

const availabilityDocMatchesDate = (data: { date?: unknown } | undefined, date: Date): boolean => {
  if (!data?.date) {
    return false;
  }

  return getDateKey(toLocalCalendarDate(timestampToDate(data.date))) === getDateKey(date);
};

const resolveAvailabilityDoc = async (date: Date) => {
  const calendarDate = toLocalCalendarDate(date);
  const dayStart = calendarDate;
  const dayEnd = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate(), 23, 59, 59, 999);

  const dayQuery = query(
    availabilityCollection,
    where('date', '>=', dateToTimestamp(dayStart)),
    where('date', '<=', dateToTimestamp(dayEnd))
  );
  const daySnapshot = await getDocs(dayQuery);
  const matchingDoc = daySnapshot.docs.find((docSnap) =>
    availabilityDocMatchesDate(docSnap.data(), calendarDate)
  );
  if (matchingDoc) {
    return { id: matchingDoc.id, ref: matchingDoc.ref, snap: matchingDoc };
  }

  const preferredId = createAvailabilityDateId(calendarDate);
  const preferredRef = doc(availabilityCollection, preferredId);
  const preferredSnap = await getDoc(preferredRef);
  if (!preferredSnap.exists() || availabilityDocMatchesDate(preferredSnap.data(), calendarDate)) {
    return { id: preferredId, ref: preferredRef, snap: preferredSnap };
  }

  // Preferred id is already used by another calendar day (legacy UTC ids).
  const fallbackId = `local-${preferredId}`;
  const fallbackRef = doc(availabilityCollection, fallbackId);
  const fallbackSnap = await getDoc(fallbackRef);
  return { id: fallbackId, ref: fallbackRef, snap: fallbackSnap };
};

// Helper function to process time slots for Firestore
const processTimeSlotsForFirestore = (timeSlots?: TimeSlot[]): any[] | undefined => {
  if (!timeSlots) return undefined;
  
  return timeSlots.map(slot => ({
    ...slot,
    // Ensure all fields are properly formatted
    startTime: slot.startTime,
    endTime: slot.endTime,
    isAvailable: slot.isAvailable,
    maxBookings: slot.maxBookings || null,
    currentBookings: slot.currentBookings || 0,
    label: slot.label || null
  }));
};

// Helper function to process time slots from Firestore
const processTimeSlotsFromFirestore = (timeSlots?: any[]): TimeSlot[] | undefined => {
  if (!timeSlots) return undefined;
  
  return timeSlots.map(slot => ({
    id: slot.id,
    startTime: slot.startTime,
    endTime: slot.endTime,
    isAvailable: slot.isAvailable,
    maxBookings: slot.maxBookings,
    currentBookings: slot.currentBookings || 0,
    label: slot.label
  }));
};

// Create a new availability date
export const createAvailabilityDate = async (date: Date, availabilityData: Partial<AvailabilityDate> = {}): Promise<AvailabilityDate> => {
  const calendarDate = toLocalCalendarDate(date);
  const { id: dateId, ref: docRef, snap } = await resolveAvailabilityDoc(calendarDate);
  
  // Check if availability already exists for this date
  if (snap.exists()) {
    throw new Error(`Availability already exists for date ${dateId}`);
  }
  
  const newAvailability: any = {
    id: dateId,
    date: dateToTimestamp(calendarDate),
    isAvailable: availabilityData.isAvailable !== undefined ? availabilityData.isAvailable : true,
    maxBookings: availabilityData.maxBookings || null,
    currentBookings: availabilityData.currentBookings || 0,
    timeSlots: processTimeSlotsForFirestore(availabilityData.timeSlots) || [],
    notes: availabilityData.notes || null,
    isBlocked: availabilityData.isBlocked || false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  
  await setDoc(docRef, newAvailability);
  
  return {
    id: dateId,
    date: calendarDate,
    isAvailable: newAvailability.isAvailable,
    maxBookings: newAvailability.maxBookings,
    currentBookings: newAvailability.currentBookings,
    timeSlots: availabilityData.timeSlots,
    notes: newAvailability.notes,
    isBlocked: newAvailability.isBlocked,
    createdAt: new Date(),
    updatedAt: new Date()
  } as AvailabilityDate;
};

// Get availability for a specific date
export const getAvailabilityDate = async (date: Date): Promise<AvailabilityDate | null> => {
  const { snap: docSnap } = await resolveAvailabilityDoc(date);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  const data = docSnap.data();
  
  return {
    id: docSnap.id,
    date: toLocalCalendarDate(timestampToDate(data.date)),
    isAvailable: data.isAvailable,
    maxBookings: data.maxBookings,
    currentBookings: data.currentBookings || 0,
    timeSlots: processTimeSlotsFromFirestore(data.timeSlots),
    notes: data.notes,
    isBlocked: data.isBlocked || false,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt)
  } as AvailabilityDate;
};

// Update availability for a specific date
export const updateAvailabilityDate = async (date: Date, updates: Partial<AvailabilityDate>): Promise<void> => {
  const calendarDate = toLocalCalendarDate(date);
  const { ref: docRef, snap: existingDoc } = await resolveAvailabilityDoc(calendarDate);
  
  // Check if document exists, create if it doesn't
  if (!existingDoc.exists()) {
    // Create new availability date with the updates
    await createAvailabilityDate(calendarDate, updates);
    return;
  }
  
  const updateData: any = {
    updatedAt: serverTimestamp()
  };
  
  // Add fields that are being updated
  if (updates.isAvailable !== undefined) {
    updateData.isAvailable = updates.isAvailable;
  }
  if (updates.maxBookings !== undefined) {
    updateData.maxBookings = updates.maxBookings;
  }
  if (updates.currentBookings !== undefined) {
    updateData.currentBookings = updates.currentBookings;
  }
  if (updates.timeSlots !== undefined) {
    const processedTimeSlots = processTimeSlotsForFirestore(updates.timeSlots);
    if (processedTimeSlots !== undefined) {
      updateData.timeSlots = processedTimeSlots;
    }
  }
  if (updates.notes !== undefined) {
    updateData.notes = updates.notes;
  }
  if (updates.isBlocked !== undefined) {
    updateData.isBlocked = updates.isBlocked;
  }
  
  await updateDoc(docRef, updateData);
};

export const upsertAvailabilityBookingCount = async (
  date: Date,
  currentBookings: number
): Promise<void> => {
  const calendarDate = toLocalCalendarDate(date);
  const { id, ref, snap } = await resolveAvailabilityDoc(calendarDate);

  if (!snap.exists()) {
    await setDoc(ref, {
      id,
      date: dateToTimestamp(calendarDate),
      isAvailable: true,
      maxBookings: null,
      currentBookings,
      timeSlots: [],
      notes: null,
      isBlocked: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return;
  }

  await updateDoc(ref, {
    date: dateToTimestamp(calendarDate),
    currentBookings,
    updatedAt: serverTimestamp()
  });
};

// Delete availability for a specific date
export const deleteAvailabilityDate = async (date: Date): Promise<void> => {
  const calendarDate = toLocalCalendarDate(date);
  const { ref: docRef, snap } = await resolveAvailabilityDoc(calendarDate);
  if (snap.exists()) {
    await deleteDoc(docRef);
  }
};

// Get availability for a range of dates
export const getAvailabilityRange = async (startDate: Date, endDate: Date): Promise<AvailabilityDate[]> => {
  // Create date range query
  const startTimestamp = dateToTimestamp(startDate);
  const endTimestamp = dateToTimestamp(endDate);
  
  const q = query(
    availabilityCollection,
    where('date', '>=', startTimestamp),
    where('date', '<=', endTimestamp),
    orderBy('date', 'asc')
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      date: toLocalCalendarDate(timestampToDate(data.date)),
      isAvailable: data.isAvailable,
      maxBookings: data.maxBookings,
      currentBookings: data.currentBookings || 0,
      timeSlots: processTimeSlotsFromFirestore(data.timeSlots),
      notes: data.notes,
      isBlocked: data.isBlocked || false,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt)
    } as AvailabilityDate;
  });
};

// Bulk update availability for multiple dates
export const bulkUpdateAvailability = async (dates: Date[], updates: Partial<AvailabilityDate>): Promise<void> => {
  const batch = [];
  
  for (const date of dates) {
    const calendarDate = toLocalCalendarDate(date);
    const { id: dateId, ref: docRef, snap: existingDoc } = await resolveAvailabilityDoc(calendarDate);
    
    if (existingDoc.exists()) {
      // Update existing document
      const updateData: any = {
        updatedAt: serverTimestamp()
      };
      
      if (updates.isAvailable !== undefined) updateData.isAvailable = updates.isAvailable;
      if (updates.maxBookings !== undefined) updateData.maxBookings = updates.maxBookings;
      if (updates.currentBookings !== undefined) updateData.currentBookings = updates.currentBookings;
      if (updates.timeSlots !== undefined) {
        const processedTimeSlots = processTimeSlotsForFirestore(updates.timeSlots);
        if (processedTimeSlots !== undefined) {
          updateData.timeSlots = processedTimeSlots;
        }
      }
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if (updates.isBlocked !== undefined) updateData.isBlocked = updates.isBlocked;
      
      batch.push(updateDoc(docRef, updateData));
    } else {
      // Create new document
      const newAvailability: any = {
        id: dateId,
        date: dateToTimestamp(calendarDate),
        isAvailable: updates.isAvailable !== undefined ? updates.isAvailable : true,
        maxBookings: updates.maxBookings || null,
        currentBookings: updates.currentBookings || 0,
        timeSlots: processTimeSlotsForFirestore(updates.timeSlots) || [],
        notes: updates.notes || null,
        isBlocked: updates.isBlocked || false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      batch.push(setDoc(docRef, newAvailability));
    }
  }
  
  // Execute all operations
  await Promise.all(batch);
};

// Get all availability dates (with pagination)
export const getAllAvailabilityDates = async (limitCount: number = 100): Promise<AvailabilityDate[]> => {
  const q = query(
    availabilityCollection,
    orderBy('date', 'asc'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      date: toLocalCalendarDate(timestampToDate(data.date)),
      isAvailable: data.isAvailable,
      maxBookings: data.maxBookings,
      currentBookings: data.currentBookings || 0,
      timeSlots: processTimeSlotsFromFirestore(data.timeSlots),
      notes: data.notes,
      isBlocked: data.isBlocked || false,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt)
    } as AvailabilityDate;
  });
};

// ============================================================================
// AVAILABILITY SETTINGS FUNCTIONS
// ============================================================================

// Get availability settings
export const getAvailabilitySettings = async (): Promise<AvailabilitySettings | null> => {
  const docRef = doc(availabilitySettingsCollection, 'default');
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  const data = docSnap.data();
  
  return normalizeAvailabilitySettings({
    id: docSnap.id,
    defaultMaxBookings: data.defaultMaxBookings,
    weekdayMaxBookings: data.weekdayMaxBookings,
    weekendMaxBookings: data.weekendMaxBookings,
    advanceBookingDays: data.advanceBookingDays,
    workingDays: data.workingDays,
    workingHours: data.workingHours,
    blockedDates: data.blockedDates?.map((date: any) => timestampToDate(date)) || [],
    timeSlotDuration: data.timeSlotDuration,
    enableTimeSlots: data.enableTimeSlots,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt)
  });
};

// Create or update availability settings
export const updateAvailabilitySettings = async (settings: Partial<AvailabilitySettings>): Promise<void> => {
  const docRef = doc(availabilitySettingsCollection, 'default');
  
  // Check if settings exist
  const existingDoc = await getDoc(docRef);
  
  const updateData: any = {
    updatedAt: serverTimestamp()
  };
  
  // Add fields that are being updated
  if (settings.defaultMaxBookings !== undefined) {
    updateData.defaultMaxBookings = settings.defaultMaxBookings;
  }
  if (settings.weekdayMaxBookings !== undefined) {
    updateData.weekdayMaxBookings = settings.weekdayMaxBookings;
  }
  if (settings.weekendMaxBookings !== undefined) {
    updateData.weekendMaxBookings = settings.weekendMaxBookings;
  }
  if (settings.advanceBookingDays !== undefined) {
    updateData.advanceBookingDays = settings.advanceBookingDays;
  }
  if (settings.workingDays !== undefined) {
    updateData.workingDays = settings.workingDays;
  }
  if (settings.workingHours !== undefined) {
    updateData.workingHours = settings.workingHours;
  }
  if (settings.blockedDates !== undefined) {
    updateData.blockedDates = settings.blockedDates.map(date => dateToTimestamp(date));
  }
  if (settings.timeSlotDuration !== undefined) {
    updateData.timeSlotDuration = settings.timeSlotDuration;
  }
  if (settings.enableTimeSlots !== undefined) {
    updateData.enableTimeSlots = settings.enableTimeSlots;
  }
  
  if (existingDoc.exists()) {
    // Update existing settings
    await updateDoc(docRef, updateData);
  } else {
    // Create new settings
    await setDoc(docRef, {
      ...updateData,
      createdAt: serverTimestamp()
    });
  }
};

// Initialize default availability settings
export const initializeDefaultAvailabilitySettings = async (): Promise<AvailabilitySettings> => {
  const defaultSettings = createDefaultAvailabilitySettings();
  
  await updateAvailabilitySettings(defaultSettings);
  return defaultSettings;
};

// ============================================================================
// AVAILABILITY UTILITY FUNCTIONS
// ============================================================================

// Check if a date is available for booking
export const isDateAvailableForBooking = async (date: Date): Promise<boolean> => {
  const availability = await getAvailabilityDate(date);
  
  if (!availability) {
    // If no specific availability record, check against default settings
    const settings = await getAvailabilitySettings();
    if (!settings) return true; // Default to available if no settings
    
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    
    // Check if date is within advance booking limit
    const maxAdvanceDate = new Date();
    maxAdvanceDate.setDate(maxAdvanceDate.getDate() + settings.advanceBookingDays);
    if (date > maxAdvanceDate) return false;
    
    // Check if date is a working day
    const dayOfWeek = date.getDay();
    if (!settings.workingDays.includes(dayOfWeek)) return false;
    
    // Check if date is in blocked dates
    const isBlocked = settings.blockedDates.some(blockedDate => 
      blockedDate.toDateString() === date.toDateString()
    );
    if (isBlocked) return false;

    const maxBookings = resolveMaxBookingsForDate(date, settings);
    if (maxBookings != null && maxBookings <= 0) return false;
    
    return true;
  }
  
  // Check specific availability record
  if (availability.isBlocked || !availability.isAvailable) {
    return false;
  }
  
  const settings = await getAvailabilitySettings();
  const maxBookings = resolveMaxBookingsForDate(date, settings, availability);
  if (maxBookings != null && availability.currentBookings >= maxBookings) {
    return false;
  }
  
  return true;
};

// Get available time slots for a specific date
export const getAvailableTimeSlots = async (date: Date): Promise<TimeSlot[]> => {
  const availability = await getAvailabilityDate(date);
  
  if (!availability || !availability.timeSlots) {
    return [];
  }
  
  return availability.timeSlots.filter(slot => 
    slot.isAvailable && 
    (!slot.maxBookings || slot.currentBookings < slot.maxBookings)
  );
};

// Increment booking count for a date
export const incrementBookingCount = async (date: Date, timeSlotId?: string): Promise<void> => {
  const availability = await getAvailabilityDate(date);
  
  if (!availability) {
    // Create new availability record
    await createAvailabilityDate(date, {
      currentBookings: 1,
      timeSlots: timeSlotId ? [{
        id: timeSlotId,
        startTime: '09:00',
        endTime: '10:00',
        isAvailable: true,
        currentBookings: 1
      }] : undefined
    });
    return;
  }
  
  const updates: Partial<AvailabilityDate> = {
    currentBookings: availability.currentBookings + 1
  };
  
  // Update specific time slot if provided
  if (timeSlotId && availability.timeSlots) {
    updates.timeSlots = availability.timeSlots.map(slot => 
      slot.id === timeSlotId 
        ? { ...slot, currentBookings: slot.currentBookings + 1 }
        : slot
    );
  }
  
  await updateAvailabilityDate(date, updates);
};

// Decrement booking count for a date
export const decrementBookingCount = async (date: Date, timeSlotId?: string): Promise<void> => {
  const availability = await getAvailabilityDate(date);
  
  if (!availability) {
    return; // Nothing to decrement
  }
  
  const updates: Partial<AvailabilityDate> = {
    currentBookings: Math.max(0, availability.currentBookings - 1)
  };
  
  // Update specific time slot if provided
  if (timeSlotId && availability.timeSlots) {
    updates.timeSlots = availability.timeSlots.map(slot => 
      slot.id === timeSlotId 
        ? { ...slot, currentBookings: Math.max(0, slot.currentBookings - 1) }
        : slot
    );
  }
  
  await updateAvailabilityDate(date, updates);
};

// ============================================================================
// DELIVERY PRICING FUNCTIONS - Delivery Tier Management
// ============================================================================

// Helper function to process delivery tier for Firestore
const processDeliveryTierForFirestore = (tier: Partial<DeliveryTier>): any => {
  return {
    ...tier,
    createdAt: tier.createdAt ? dateToTimestamp(tier.createdAt) : serverTimestamp(),
    updatedAt: serverTimestamp()
  };
};

// Helper function to process delivery tier from Firestore
const processDeliveryTierFromFirestore = (doc: any): DeliveryTier => {
  const data = doc.data();
  return {
    id: doc.id,
    name: data.name,
    price: data.price,
    locations: data.locations || [],
    isActive: data.isActive,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt)
  } as DeliveryTier;
};

// Create a new delivery tier
export const createDeliveryTier = async (tierData: Partial<DeliveryTier>): Promise<DeliveryTier> => {
  const newTier = processDeliveryTierForFirestore(tierData);
  
  const docRef = await addDoc(deliveryTiersCollection, newTier);
  
  return {
    id: docRef.id,
    name: tierData.name || '',
    price: tierData.price || 0,
    locations: tierData.locations || [],
    isActive: tierData.isActive !== undefined ? tierData.isActive : true,
    createdAt: new Date(),
    updatedAt: new Date()
  } as DeliveryTier;
};

// Get all delivery tiers
export const getDeliveryTiers = async (): Promise<DeliveryTier[]> => {
  const snapshot = await getDocs(query(deliveryTiersCollection, orderBy('createdAt', 'desc')));
  
  return snapshot.docs.map(processDeliveryTierFromFirestore);
};

// Get a specific delivery tier
export const getDeliveryTier = async (tierId: string): Promise<DeliveryTier | null> => {
  const docRef = doc(deliveryTiersCollection, tierId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return processDeliveryTierFromFirestore(docSnap);
};

// Update a delivery tier
export const updateDeliveryTier = async (tierId: string, updates: Partial<DeliveryTier>): Promise<void> => {
  const docRef = doc(deliveryTiersCollection, tierId);
  
  const updateData: any = {
    updatedAt: serverTimestamp()
  };
  
  // Add fields that are being updated
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.price !== undefined) updateData.price = updates.price;
  if (updates.locations !== undefined) updateData.locations = updates.locations;
  if (updates.isActive !== undefined) updateData.isActive = updates.isActive;
  
  await updateDoc(docRef, updateData);
};

// Delete a delivery tier
export const deleteDeliveryTier = async (tierId: string): Promise<void> => {
  const docRef = doc(deliveryTiersCollection, tierId);
  await deleteDoc(docRef);
};

// Get delivery pricing settings
export const getDeliveryPricingSettings = async (): Promise<DeliveryPricingSettings | null> => {
  const docRef = doc(deliverySettingsCollection, 'default');
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  const data = docSnap.data();
  
  return {
    id: docSnap.id,
    defaultDeliveryPrice: data.defaultDeliveryPrice,
    freeDeliveryThreshold: data.freeDeliveryThreshold,
    isActive: data.isActive,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt)
  } as DeliveryPricingSettings;
};

// Create or update delivery pricing settings
export const updateDeliveryPricingSettings = async (settings: Partial<DeliveryPricingSettings>): Promise<void> => {
  const docRef = doc(deliverySettingsCollection, 'default');
  
  // Check if settings exist
  const existingDoc = await getDoc(docRef);
  
  const updateData: any = {
    updatedAt: serverTimestamp()
  };
  
  // Add fields that are being updated
  if (settings.defaultDeliveryPrice !== undefined) {
    updateData.defaultDeliveryPrice = settings.defaultDeliveryPrice;
  }
  if (settings.freeDeliveryThreshold !== undefined) {
    updateData.freeDeliveryThreshold = settings.freeDeliveryThreshold;
  }
  if (settings.isActive !== undefined) {
    updateData.isActive = settings.isActive;
  }
  
  if (existingDoc.exists()) {
    // Update existing settings
    await updateDoc(docRef, updateData);
  } else {
    // Create new settings
    await setDoc(docRef, {
      ...updateData,
      createdAt: serverTimestamp()
    });
  }
};

// Calculate delivery price for a location
export const calculateDeliveryPrice = async (location: string, orderTotal: number = 0): Promise<number> => {
  const [tiers, settings] = await Promise.all([
    getDeliveryTiers(),
    getDeliveryPricingSettings()
  ]);
  
  // Check if order qualifies for free delivery
  if (settings && orderTotal >= settings.freeDeliveryThreshold) {
    return 0;
  }
  
  // Find the tier that contains this location
  const tier = tiers.find(t => 
    t.isActive && t.locations.some(loc => 
      loc.town.toLowerCase() === location.toLowerCase()
    )
  );
  
  if (tier) {
    return tier.price;
  }
  
  // Return default price if no tier found
  return settings?.defaultDeliveryPrice || 5.00;
};

// ============================================================================
// VAT SETTINGS FUNCTIONS
// ============================================================================

// Helper function to process VAT settings for Firestore
const processVatSettingsForFirestore = (settings: Partial<VatSettings>): any => {
  return {
    ...settings,
    createdAt: settings.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp()
  };
};

// Helper function to process VAT settings from Firestore
const processVatSettingsFromFirestore = (data: any): VatSettings => {
  return {
    id: data.id,
    isEnabled: data.isEnabled || false,
    percentage: data.percentage || 0,
    isInclusive: data.isInclusive || false,
    isActive: data.isActive !== undefined ? data.isActive : true,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
};

// Get VAT settings
export const getVatSettings = async (): Promise<VatSettings | null> => {
  try {
    const docRef = doc(vatSettingsCollection, 'default');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return processVatSettingsFromFirestore({ id: docSnap.id, ...docSnap.data() });
    }
    
    return null;
  } catch (error) {
    console.error('Error getting VAT settings:', error);
    throw error;
  }
};

// Create VAT settings (initialize default settings)
export const createVatSettings = async (settings: Partial<VatSettings>): Promise<VatSettings> => {
  try {
    const newSettings = processVatSettingsForFirestore({
      ...settings,
      isEnabled: settings.isEnabled || false,
      percentage: settings.percentage || 23, // Default to 23% for Ireland
      isInclusive: settings.isInclusive || false,
      isActive: settings.isActive !== undefined ? settings.isActive : true
    });
    
    const docRef = doc(vatSettingsCollection, 'default');
    await setDoc(docRef, newSettings);
    
    return processVatSettingsFromFirestore({ id: 'default', ...newSettings });
  } catch (error) {
    console.error('Error creating VAT settings:', error);
    throw error;
  }
};

// Update VAT settings
export const updateVatSettings = async (settings: Partial<VatSettings>): Promise<void> => {
  try {
    const docRef = doc(vatSettingsCollection, 'default');
    const updateData = processVatSettingsForFirestore(settings);
    
    // Use setDoc with merge: true to create the document if it doesn't exist
    await setDoc(docRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating VAT settings:', error);
    throw error;
  }
};

// Calculate VAT amount for a given subtotal
export const calculateVatAmount = async (subtotal: number): Promise<{ vatAmount: number; vatPercentage: number; isInclusive: boolean }> => {
  try {
    const vatSettings = await getVatSettings();
    
    console.log('🧾 calculateVatAmount - VAT Settings:', vatSettings);
    console.log('🧾 calculateVatAmount - Subtotal:', subtotal);
    
    if (!vatSettings || !vatSettings.isEnabled || !vatSettings.isActive) {
      console.log('🧾 calculateVatAmount - VAT is DISABLED, returning zero VAT');
      return { vatAmount: 0, vatPercentage: 0, isInclusive: false };
    }
    
    let vatAmount: number;
    
    if (vatSettings.isInclusive) {
      // For inclusive pricing: calculate VAT amount from the inclusive price
      // VAT amount = (subtotal * vatRate) / (100 + vatRate)
      vatAmount = Math.round((subtotal * vatSettings.percentage / (100 + vatSettings.percentage)) * 100) / 100;
    } else {
      // For exclusive pricing: calculate VAT amount to be added on top
      vatAmount = Math.round((subtotal * vatSettings.percentage / 100) * 100) / 100;
    }
    
    console.log('🧾 calculateVatAmount - VAT is ENABLED, calculated VAT:', {
      vatAmount,
      vatPercentage: vatSettings.percentage,
      isInclusive: vatSettings.isInclusive
    });
    
    return {
      vatAmount,
      vatPercentage: vatSettings.percentage,
      isInclusive: vatSettings.isInclusive
    };
  } catch (error) {
    console.error('Error calculating VAT amount:', error);
    throw error;
  }
};

// Initialize default VAT settings if they don't exist
export const initializeDefaultVatSettings = async (): Promise<VatSettings> => {
  try {
    const existingSettings = await getVatSettings();
    
    if (existingSettings) {
      return existingSettings;
    }
    
    // Create default VAT settings
    return await createVatSettings({
      isEnabled: true,
      percentage: 23, // Ireland VAT rate
      isInclusive: false, // VAT added on top of prices
      isActive: true
    });
  } catch (error) {
    console.error('Error initializing default VAT settings:', error);
    throw error;
  }
};

// ============================================================================
// IMAGE ON CAKE SETTINGS FUNCTIONS
// ============================================================================

// Get image on cake settings
export const getImageOnCakeSettings = async (): Promise<ImageOnCakeSettings | null> => {
  try {
    const docRef = doc(db, 'imageOnCakeSettings', 'default');
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    
    return {
      id: docSnap.id,
      price: data.price,
      isActive: data.isActive,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt)
    } as ImageOnCakeSettings;
  } catch (error) {
    console.error('Error getting image on cake settings:', error);
    throw error;
  }
};

// Create image on cake settings (initialize default settings)
export const createImageOnCakeSettings = async (settings: Partial<ImageOnCakeSettings>): Promise<ImageOnCakeSettings> => {
  try {
    const newSettings = {
      price: settings.price || 15.00, // Default to €15
      isActive: settings.isActive !== undefined ? settings.isActive : true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = doc(db, 'imageOnCakeSettings', 'default');
    await setDoc(docRef, newSettings);
    
    return {
      id: 'default',
      price: newSettings.price,
      isActive: newSettings.isActive,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error creating image on cake settings:', error);
    throw error;
  }
};

// Update image on cake settings
export const updateImageOnCakeSettings = async (settings: Partial<ImageOnCakeSettings>): Promise<void> => {
  try {
    const docRef = doc(db, 'imageOnCakeSettings', 'default');
    const updateData: any = {
      updatedAt: serverTimestamp()
    };
    
    if (settings.price !== undefined) {
      updateData.price = settings.price;
    }
    if (settings.isActive !== undefined) {
      updateData.isActive = settings.isActive;
    }
    
    // Use setDoc with merge: true to create the document if it doesn't exist
    await setDoc(docRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating image on cake settings:', error);
    throw error;
  }
};

// Initialize default image on cake settings if they don't exist
export const initializeDefaultImageOnCakeSettings = async (): Promise<void> => {
  try {
    const existingSettings = await getImageOnCakeSettings();
    if (!existingSettings) {
      await createImageOnCakeSettings({
        price: 15.00,
        isActive: true
      });
    }
  } catch (error) {
    console.error('Error initializing default image on cake settings:', error);
    throw error;
  }
};

// ============================================================================
// COLOUR PRICING SETTINGS
// ============================================================================

const COLOR_PRICING_DOC = doc(db, 'colorPricingSettings', 'default');

const toPriceMap = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, number>>(
    (acc, [key, price]) => {
      if (typeof price === 'number' && Number.isFinite(price) && price >= 0) {
        acc[key] = price;
      }
      return acc;
    },
    {}
  );
};

const toCollectionPriceMap = (
  value: unknown
): ColorPricingSettings['collectionPrices'] => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value as Record<string, unknown>).reduce<
    ColorPricingSettings['collectionPrices']
  >((acc, [collection, prices]) => {
    acc[collection as ProductCollectionType] = toPriceMap(prices);
    return acc;
  }, {});
};

const toProductPriceMap = (
  value: unknown
): ColorPricingSettings['productPrices'] => {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value as Record<string, unknown>).reduce<
    ColorPricingSettings['productPrices']
  >((acc, [collection, colours]) => {
    if (!colours || typeof colours !== 'object') return acc;
    acc[collection as ProductCollectionType] = Object.entries(
      colours as Record<string, unknown>
    ).reduce<Record<string, Record<string, number>>>((colourAcc, [colour, products]) => {
      colourAcc[colour] = toPriceMap(products);
      return colourAcc;
    }, {});
    return acc;
  }, {});
};

const mapColorPricingDoc = (
  data: Record<string, unknown>,
  fallback: ColorPricingSettings
): ColorPricingSettings => ({
  id: 'default',
  globalPrices: toPriceMap(data.globalPrices ?? fallback.globalPrices),
  collectionPrices: toCollectionPriceMap(data.collectionPrices ?? fallback.collectionPrices),
  productPrices: toProductPriceMap(data.productPrices ?? fallback.productPrices),
  createdAt: data.createdAt ? timestampToDate(data.createdAt as Timestamp) : fallback.createdAt,
  updatedAt: data.updatedAt ? timestampToDate(data.updatedAt as Timestamp) : fallback.updatedAt
});

export const getColorPricingSettings = async (): Promise<ColorPricingSettings | null> => {
  try {
    const docSnap = await getDoc(COLOR_PRICING_DOC);
    if (!docSnap.exists()) return null;
    return mapColorPricingDoc(docSnap.data() as Record<string, unknown>, createDefaultColorPricingSettings());
  } catch (error) {
    console.error('Error getting colour pricing settings:', error);
    return null;
  }
};

export const createColorPricingSettings = async (
  settings?: Partial<ColorPricingSettings>
): Promise<ColorPricingSettings> => {
  const defaults = createDefaultColorPricingSettings();
  const newSettings = {
    globalPrices: settings?.globalPrices ?? defaults.globalPrices,
    collectionPrices: settings?.collectionPrices ?? defaults.collectionPrices,
    productPrices: settings?.productPrices ?? defaults.productPrices,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(COLOR_PRICING_DOC, newSettings);
  return {
    id: 'default',
    globalPrices: newSettings.globalPrices,
    collectionPrices: newSettings.collectionPrices,
    productPrices: newSettings.productPrices,
    createdAt: new Date(),
    updatedAt: new Date()
  };
};

export const updateColorPricingSettings = async (
  settings: Partial<ColorPricingSettings>
): Promise<void> => {
  const updateData: Record<string, unknown> = {
    updatedAt: serverTimestamp()
  };

  if (settings.globalPrices !== undefined) {
    updateData.globalPrices = settings.globalPrices;
  }
  if (settings.collectionPrices !== undefined) {
    updateData.collectionPrices = settings.collectionPrices;
  }
  if (settings.productPrices !== undefined) {
    updateData.productPrices = settings.productPrices;
  }

  await setDoc(COLOR_PRICING_DOC, updateData, { merge: true });
};

export const initializeDefaultColorPricingSettings = async (): Promise<ColorPricingSettings> => {
  const existing = await getColorPricingSettings();
  if (existing) return existing;
  return createColorPricingSettings();
};
